import React, { useMemo } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { dashboardAPI } from '../services/api';
import { DashboardOverview } from '../types';
import { usePermissions } from '../hooks/usePermissions';
import {
  UsersIcon,
  DocumentTextIcon,
  ArchiveBoxIcon,
  CurrencyDollarIcon,
  PlusIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  MinusSmallIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  ShieldCheckIcon,
  BanknotesIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { Button, Card } from '../components/ui';
import { PageHeader } from '../components/ui';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const createCurrencyFormatters = (locale: string, currency: string) => {
  const compact = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  });
  const full = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  });
  return {
    fmtMoney: (n: number) => compact.format(n || 0),
    fmtMoneyFull: (n: number) => full.format(n || 0),
  };
};

const PAYMENT_METHOD_LABEL: Record<string, string> = {
  EFECTIVO: 'Efectivo',
  TRANSFERENCIA_BANCARIA: 'Transferencia',
  TRANSFERENCIA_MOVIL: 'Pago móvil',
  CHEQUE: 'Cheque',
  DEPOSITO_BANCARIO: 'Depósito',
};

function TrendPill({
  changePercent,
  direction,
  invert = false,
}: {
  changePercent: number | null | undefined;
  direction?: string;
  invert?: boolean;
}) {
  if (changePercent == null) {
    return (
      <span className="inline-flex items-center gap-0.5 rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-600">
        <MinusSmallIcon className="h-3 w-3" />
        n/d
      </span>
    );
  }
  const up = direction === 'up';
  const good = invert ? !up : up;
  const Icon = up ? ArrowTrendingUpIcon : ArrowTrendingDownIcon;
  return (
    <span
      className={`inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-[10px] font-semibold ${
        good
          ? 'bg-emerald-50 text-emerald-800 ring-1 ring-emerald-600/15'
          : 'bg-rose-50 text-rose-800 ring-1 ring-rose-600/15'
      }`}
    >
      <Icon className="h-3 w-3" />
      {changePercent > 0 ? '+' : ''}
      {changePercent}%
    </span>
  );
}

function DashboardSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="h-24 rounded-2xl bg-slate-200/80" />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-20 rounded-xl bg-slate-200/70" />
        ))}
      </div>
      <div className="h-28 rounded-xl bg-slate-200/60" />
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <div className="h-80 rounded-2xl bg-slate-200/70 xl:col-span-2" />
        <div className="h-80 rounded-2xl bg-slate-200/60" />
      </div>
    </div>
  );
}

/* ─── KPI strip (Cobros-style) ─── */

interface DashboardKpiStripProps {
  showInvoices: boolean;
  showPayments: boolean;
  kpis: Record<string, any>;
  fmtMoneyFull: (n: number) => string;
  onNavigate: (path: string) => void;
}

const DashboardKpiStrip: React.FC<DashboardKpiStripProps> = ({
  showInvoices,
  showPayments,
  kpis,
  fmtMoneyFull,
  onNavigate,
}) => {
  const items: React.ReactNode[] = [];

  if (showInvoices && kpis.outstandingToCollect) {
    items.push(
      <button
        key="outstanding"
        type="button"
        onClick={() => onNavigate('/cobros?tab=facturas')}
        className="rounded-xl border border-slate-200/80 bg-white px-4 py-3 text-left shadow-soft transition hover:border-slate-300 hover:shadow-md"
      >
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-indigo-50">
            <BanknotesIcon className="h-4 w-4 text-indigo-600" />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-medium text-slate-500">Por cobrar</p>
            <p className="text-lg font-semibold tracking-tight text-slate-900 truncate">
              {fmtMoneyFull(kpis.outstandingToCollect.amount)}
            </p>
            <p className="text-[11px] text-slate-400">{kpis.outstandingToCollect.count ?? 0} factura(s)</p>
          </div>
        </div>
      </button>
    );
  }

  if (showInvoices && kpis.overdueInvoices) {
    items.push(
      <button
        key="overdue"
        type="button"
        onClick={() => onNavigate('/cobros?tab=facturas')}
        className="rounded-xl border border-slate-200/80 bg-white px-4 py-3 text-left shadow-soft transition hover:border-rose-200 hover:shadow-md"
      >
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-rose-50">
            <ExclamationTriangleIcon className="h-4 w-4 text-rose-600" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs font-medium text-slate-500">Vencido</p>
              <TrendPill
                changePercent={kpis.overdueInvoices.changePercent}
                direction={kpis.overdueInvoices.direction}
                invert
              />
            </div>
            <p className="text-lg font-semibold tracking-tight text-rose-700 truncate">
              {fmtMoneyFull(kpis.overdueInvoices.amount ?? 0)}
            </p>
            <p className="text-[11px] text-rose-600/80">{kpis.overdueInvoices.count ?? 0} factura(s)</p>
          </div>
        </div>
      </button>
    );
  }

  if (showPayments && kpis.paymentsToday) {
    items.push(
      <button
        key="today"
        type="button"
        onClick={() => onNavigate('/payments')}
        className="rounded-xl border border-slate-200/80 bg-white px-4 py-3 text-left shadow-soft transition hover:border-slate-300 hover:shadow-md"
      >
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald-50">
            <CurrencyDollarIcon className="h-4 w-4 text-emerald-600" />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-medium text-slate-500">Cobrado hoy</p>
            <p className="text-lg font-semibold tracking-tight text-emerald-700 truncate">
              {fmtMoneyFull(kpis.paymentsToday.amount)}
            </p>
            <p className="text-[11px] text-slate-400">{kpis.paymentsToday.count ?? 0} pago(s)</p>
          </div>
        </div>
      </button>
    );
  }

  if (showPayments && kpis.paymentsThisMonth) {
    items.push(
      <button
        key="month"
        type="button"
        onClick={() => onNavigate('/payments')}
        className="rounded-xl border border-slate-200/80 bg-white px-4 py-3 text-left shadow-soft transition hover:border-slate-300 hover:shadow-md"
      >
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100">
            <CurrencyDollarIcon className="h-4 w-4 text-slate-700" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs font-medium text-slate-500">Cobrado este mes</p>
              <TrendPill
                changePercent={kpis.paymentsThisMonth.changePercent}
                direction={kpis.paymentsThisMonth.direction}
              />
            </div>
            <p className="text-lg font-semibold tracking-tight text-slate-900 truncate">
              {fmtMoneyFull(kpis.paymentsThisMonth.amount)}
            </p>
            <p className="text-[11px] text-slate-400">
              vs mes ant. {fmtMoneyFull(kpis.paymentsThisMonth.previousAmount ?? 0)}
            </p>
          </div>
        </div>
      </button>
    );
  }

  if (items.length === 0) return null;

  const cols =
    items.length >= 4
      ? 'sm:grid-cols-2 xl:grid-cols-4'
      : items.length === 3
        ? 'sm:grid-cols-3'
        : items.length === 2
          ? 'sm:grid-cols-2'
          : '';

  return <div className={`grid grid-cols-1 gap-3 ${cols}`}>{items}</div>;
};

/* ─── Alerts list ─── */

interface DashboardAlertsProps {
  alerts: DashboardOverview['alerts'];
  onNavigate: (path: string) => void;
}

const DashboardAlerts: React.FC<DashboardAlertsProps> = ({ alerts, onNavigate }) => {
  if (!alerts || alerts.length === 0) return null;

  return (
    <section className="overflow-hidden rounded-xl border border-slate-200/80 bg-white shadow-soft">
      <div className="border-b border-slate-100 bg-slate-50/80 px-4 py-3">
        <div className="flex items-center gap-2">
          <ExclamationTriangleIcon className="h-4 w-4 text-amber-600" />
          <h2 className="text-sm font-semibold text-slate-900">Requiere atención</h2>
          <span className="rounded-md bg-slate-200/80 px-1.5 py-0.5 text-[10px] font-semibold text-slate-700">
            {alerts.length}
          </span>
        </div>
      </div>
      <ul className="divide-y divide-slate-100">
        {alerts.map((a) => {
          const border =
            a.severity === 'high'
              ? 'border-l-rose-500'
              : a.severity === 'medium'
                ? 'border-l-amber-400'
                : 'border-l-slate-300';
          const badge =
            a.severity === 'high'
              ? 'bg-rose-50 text-rose-800'
              : a.severity === 'medium'
                ? 'bg-amber-50 text-amber-900'
                : 'bg-slate-100 text-slate-700';
          return (
            <li key={a.id}>
              <button
                type="button"
                onClick={() => a.link && onNavigate(a.link)}
                className={`flex w-full items-start gap-3 border-l-4 ${border} px-4 py-3 text-left transition hover:bg-slate-50/80 ${
                  a.link ? 'cursor-pointer' : 'cursor-default'
                }`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-semibold text-slate-900">{a.title}</p>
                    <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${badge}`}>
                      {a.severity === 'high' ? 'Alta' : a.severity === 'medium' ? 'Media' : 'Info'}
                    </span>
                  </div>
                  <p className="mt-0.5 text-xs text-slate-500">{a.message}</p>
                </div>
                {a.link && <ChevronRightIcon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />}
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

/* ─── Operational stats (secondary) ─── */

interface DashboardOpStatsProps {
  showClients: boolean;
  showCaskets: boolean;
  kpis: Record<string, any>;
  onNavigate: (path: string) => void;
}

const DashboardOpStats: React.FC<DashboardOpStatsProps> = ({
  showClients,
  showCaskets,
  kpis,
  onNavigate,
}) => {
  const rows: Array<{
    key: string;
    label: string;
    value: string | number;
    icon: React.ReactNode;
    onClick?: () => void;
  }> = [];

  if (showClients && kpis.newClientsThisMonth != null) {
    rows.push({
      key: 'clients',
      label: 'Clientes nuevos (mes)',
      value: kpis.newClientsThisMonth.value ?? 0,
      icon: <UsersIcon className="h-3.5 w-3.5 text-indigo-600" />,
      onClick: () => onNavigate('/clients'),
    });
  }

  if (kpis.activePlans != null) {
    rows.push({
      key: 'plans',
      label: 'Planes activos',
      value: kpis.activePlans,
      icon: <DocumentTextIcon className="h-3.5 w-3.5 text-slate-600" />,
    });
  }

  if (showCaskets && kpis.inventoryLowModels != null) {
    rows.push({
      key: 'stock',
      label: 'Stock bajo',
      value: kpis.inventoryLowModels,
      icon: <ArchiveBoxIcon className="h-3.5 w-3.5 text-amber-700" />,
      onClick: () => onNavigate('/coffins'),
    });
  }

  if (kpis.serviceActivationsPending != null) {
    rows.push({
      key: 'services',
      label: 'Servicios pendientes',
      value: kpis.serviceActivationsPending,
      icon: <ClockIcon className="h-3.5 w-3.5 text-slate-600" />,
      onClick: () => onNavigate('/service-activations'),
    });
  }

  if (rows.length === 0) return null;

  return (
    <Card className="overflow-hidden border-slate-200/80 p-0 shadow-soft">
      <div className="border-b border-slate-100 bg-slate-50/80 px-4 py-3">
        <h3 className="text-sm font-semibold text-slate-900">Operación</h3>
      </div>
      <ul className="divide-y divide-slate-100">
        {rows.map((row) => {
          const content = (
            <>
              <span className="flex h-7 w-7 items-center justify-center rounded-md bg-slate-100">{row.icon}</span>
              <span className="min-w-0 flex-1 text-xs font-medium text-slate-600">{row.label}</span>
              <span className="text-sm font-semibold tabular-nums text-slate-900">{row.value}</span>
            </>
          );
          return (
            <li key={row.key}>
              {row.onClick ? (
                <button
                  type="button"
                  onClick={row.onClick}
                  className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition hover:bg-slate-50/80"
                >
                  {content}
                </button>
              ) : (
                <div className="flex items-center gap-3 px-4 py-2.5">{content}</div>
              )}
            </li>
          );
        })}
      </ul>
    </Card>
  );
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  const { data: axiosRes, isLoading, error, isFetching } = useQuery(
    'dashboard-overview',
    () => dashboardAPI.get(),
    { refetchInterval: 60000 }
  );

  const dash = axiosRes?.data?.data as DashboardOverview | undefined;
  const v = dash?.visibility;
  const locale = dash?.meta?.locale || 'es-DO';
  const currencyCode = dash?.meta?.currencyCode || 'DOP';
  const { fmtMoneyFull } = createCurrencyFormatters(locale, currencyCode);

  const todayLabel = useMemo(() => {
    return new Date().toLocaleDateString('es-VE', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  }, []);

  if (isLoading && !dash) {
    return (
      <div className="max-w-[1600px] mx-auto space-y-6 animate-fade-in-up">
        <DashboardSkeleton />
      </div>
    );
  }

  if (error || !dash) {
    return (
      <div className="text-center py-16">
        <div className="mx-auto max-w-md rounded-2xl border border-rose-200 bg-rose-50/80 px-6 py-8">
          <ExclamationTriangleIcon className="mx-auto h-10 w-10 text-rose-600" />
          <p className="mt-3 text-sm font-semibold text-rose-900">No se pudo cargar el panel</p>
          <p className="mt-1 text-sm text-rose-700">Intente de nuevo en unos segundos.</p>
          <Button variant="outline" className="mt-4" onClick={() => window.location.reload()}>
            Reintentar
          </Button>
        </div>
      </div>
    );
  }

  const kpis = dash.kpis as Record<string, any>;

  const showInvoices = v?.invoices !== false && hasPermission('invoices.read');
  const showPayments = v?.payments !== false && hasPermission('payments.read');
  const showClients = v?.clients !== false && hasPermission('clients.read');
  const showCaskets = v?.caskets !== false && hasPermission('caskets.read');
  const showPlans = v?.planTemplates !== false && hasPermission('plan_templates.read');
  const showAudit = Boolean(v?.auditActivity);

  return (
    <div className="max-w-[1600px] mx-auto space-y-6 animate-fade-in-up pb-10">
      <PageHeader
        title="Panel operativo"
        subtitle={`${dash.meta?.companyName ? `${dash.meta.companyName} · ` : ''}${todayLabel} · Vista ${dash.meta?.roleFocus || 'general'}`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {isFetching && !isLoading && (
              <span className="text-xs text-slate-500">Actualizando…</span>
            )}
            <span className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700 ring-1 ring-slate-200/80">
              <ShieldCheckIcon className="h-3.5 w-3.5 text-slate-500" />
              Datos en vivo
            </span>
            <Button
              variant="outline"
              size="md"
              className="border-slate-300 text-slate-800"
              onClick={() => navigate('/reports')}
            >
              <ChartBarIcon className="h-4 w-4 mr-2" />
              Reportes
            </Button>
            <Button
              variant="primary"
              size="md"
              className="bg-slate-900 hover:bg-slate-800 text-white"
              onClick={() => navigate('/clients')}
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Nuevo cliente
            </Button>
          </div>
        }
      />

      <DashboardKpiStrip
        showInvoices={showInvoices}
        showPayments={showPayments}
        kpis={kpis}
        fmtMoneyFull={fmtMoneyFull}
        onNavigate={navigate}
      />

      <DashboardAlerts alerts={dash.alerts} onNavigate={navigate} />

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        {/* Columna principal: cobranza primero */}
        <div className="space-y-6 xl:col-span-2">
          {showInvoices && (
            <Card className="overflow-hidden border-slate-200/80 p-0 shadow-soft">
              <div className="border-b border-slate-100 bg-slate-50/80 px-5 py-4">
                <h3 className="text-base font-semibold text-slate-900">Cobranza y riesgo</h3>
                <p className="text-sm text-slate-500">Prioridad: morosidad y vencimientos próximos</p>
              </div>
              <div className="grid grid-cols-1 gap-6 p-5 md:grid-cols-2">
                <div>
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Top morosos
                  </h4>
                  <ul className="mt-3 space-y-2">
                    {dash.collections.topDelinquent.length === 0 ? (
                      <li className="rounded-lg border border-dashed border-slate-200 py-8 text-center text-sm text-slate-500">
                        Sin saldos pendientes destacados
                      </li>
                    ) : (
                      dash.collections.topDelinquent.map((row) => (
                        <li key={row.clientId}>
                          <button
                            type="button"
                            onClick={() => navigate(`/clients/${row.clientId}`)}
                            className="flex w-full items-center justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50/50 px-3 py-2.5 text-left transition hover:border-slate-200 hover:bg-slate-50"
                          >
                            <div className="min-w-0">
                              <p className="truncate text-sm font-medium text-slate-900">
                                {row.clientName}
                              </p>
                              <p className="text-xs text-slate-500">
                                {row.pendingInvoiceCount} fact. pend.
                                {row.lastPaymentAt
                                  ? ` · último pago ${new Date(row.lastPaymentAt).toLocaleDateString('es-VE')}`
                                  : ''}
                              </p>
                            </div>
                            <span className="shrink-0 text-sm font-semibold text-rose-700">
                              {fmtMoneyFull(row.pendingAmount)}
                            </span>
                          </button>
                        </li>
                      ))
                    )}
                  </ul>
                </div>
                <div>
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Próximas a vencer (14 días)
                  </h4>
                  <ul className="mt-3 space-y-2">
                    {dash.collections.upcomingInvoices.length === 0 ? (
                      <li className="rounded-lg border border-dashed border-slate-200 py-8 text-center text-sm text-slate-500">
                        No hay facturas pendientes en esta ventana
                      </li>
                    ) : (
                      dash.collections.upcomingInvoices.map((inv) => (
                        <li key={inv.id}>
                          <button
                            type="button"
                            onClick={() => navigate(`/invoices/${inv.id}`)}
                            className="flex w-full items-center justify-between gap-3 rounded-xl border border-slate-100 bg-white px-3 py-2.5 text-left shadow-soft transition hover:border-slate-200 hover:bg-slate-50/80"
                          >
                            <div className="min-w-0">
                              <p className="truncate text-sm font-medium text-slate-900">
                                {inv.client?.name || 'Cliente'}
                              </p>
                              <p className="text-xs text-slate-500">
                                Vence {new Date(inv.dueDate).toLocaleDateString('es-VE')} ·{' '}
                                {inv.daysRemaining} días
                              </p>
                            </div>
                            <span className="shrink-0 text-sm font-semibold text-slate-800">
                              {fmtMoneyFull(inv.amountDue)}
                            </span>
                          </button>
                        </li>
                      ))
                    )}
                  </ul>
                </div>
              </div>
            </Card>
          )}

          {showPayments && dash.charts.monthlyRevenue.length > 0 && (
            <Card className="overflow-hidden border-slate-200/80 p-0 shadow-soft">
              <div className="border-b border-slate-100 bg-slate-50/80 px-5 py-4">
                <h3 className="text-base font-semibold text-slate-900">Ingresos por mes</h3>
                <p className="text-sm text-slate-500">Últimos 6 meses</p>
              </div>
              <div className="h-72 p-4">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={dash.charts.monthlyRevenue}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis
                      dataKey="month"
                      tick={{ fontSize: 11, fill: '#64748b' }}
                      tickFormatter={(m) => m.slice(5)}
                    />
                    <YAxis tick={{ fontSize: 11, fill: '#64748b' }} />
                    <Tooltip
                      formatter={(v: number) => [fmtMoneyFull(v), 'Ingresos']}
                      labelFormatter={(l) => `Mes ${l}`}
                      contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0' }}
                    />
                    <Line
                      type="monotone"
                      dataKey="revenue"
                      stroke="#0f172a"
                      strokeWidth={2}
                      dot={{ r: 3 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Card>
          )}
        </div>

        {/* Columna lateral */}
        <div className="space-y-4">
          {dash.blocks.notifications && dash.blocks.notifications.unreadCount > 0 && (
            <button
              type="button"
              onClick={() => navigate('/notifications')}
              className="w-full rounded-xl border border-indigo-200/80 bg-indigo-50/60 px-4 py-3 text-left shadow-soft transition hover:bg-indigo-50"
            >
              <p className="text-xs font-semibold uppercase tracking-wide text-indigo-900/80">
                Notificaciones
              </p>
              <p className="mt-1 text-sm text-indigo-950">
                Tienes <span className="font-bold">{dash.blocks.notifications.unreadCount}</span>{' '}
                sin leer
              </p>
              <p className="mt-1 text-xs text-indigo-800/80">Abrir bandeja →</p>
            </button>
          )}

          {showPayments && dash.blocks.recentPayments.length > 0 && (
            <Card className="overflow-hidden border-slate-200/80 p-0 shadow-soft">
              <div className="border-b border-slate-100 bg-slate-50/80 px-4 py-3">
                <h3 className="text-sm font-semibold text-slate-900">Pagos recientes</h3>
              </div>
              <ul className="divide-y divide-slate-100">
                {dash.blocks.recentPayments.map((p) => (
                  <li
                    key={p.id}
                    className="flex items-center justify-between gap-2 px-4 py-3 hover:bg-slate-50/80"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-slate-900">
                        {p.client?.name || '—'}
                      </p>
                      <p className="text-xs text-slate-500">
                        {new Date(p.paymentDate).toLocaleString('es-VE', {
                          dateStyle: 'short',
                          timeStyle: 'short',
                        })}{' '}
                        · {PAYMENT_METHOD_LABEL[p.paymentMethod] || p.paymentMethod}
                      </p>
                    </div>
                    <span className="shrink-0 text-sm font-semibold text-emerald-700">
                      {fmtMoneyFull(p.amount)}
                    </span>
                  </li>
                ))}
              </ul>
              <div className="border-t border-slate-100 p-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full text-slate-700"
                  onClick={() => navigate('/payments')}
                >
                  Ver todos
                </Button>
              </div>
            </Card>
          )}

          <DashboardOpStats
            showClients={showClients}
            showCaskets={showCaskets}
            kpis={kpis}
            onNavigate={navigate}
          />

          {showClients && dash.blocks.recentClients.length > 0 && (
            <Card className="overflow-hidden border-slate-200/80 p-0 shadow-soft">
              <div className="border-b border-slate-100 bg-slate-50/80 px-4 py-3">
                <h3 className="text-sm font-semibold text-slate-900">Clientes nuevos</h3>
              </div>
              <ul className="divide-y divide-slate-100">
                {dash.blocks.recentClients.map((c) => (
                  <li key={c.id}>
                    <button
                      type="button"
                      onClick={() => navigate(`/clients/${c.id}`)}
                      className="w-full px-4 py-3 text-left hover:bg-slate-50/80"
                    >
                      <p className="text-sm font-medium text-slate-900">{c.name}</p>
                      <p className="text-xs text-slate-500">
                        {c.cedula} · {new Date(c.createdAt).toLocaleDateString('es-VE')}
                      </p>
                    </button>
                  </li>
                ))}
              </ul>
            </Card>
          )}

          {dash.blocks.serviceActivations.recent.length > 0 && (
            <Card className="overflow-hidden border-slate-200/80 p-0 shadow-soft">
              <div className="border-b border-slate-100 bg-slate-50/80 px-4 py-3">
                <h3 className="text-sm font-semibold text-slate-900">Servicios en curso</h3>
              </div>
              <ul className="divide-y divide-slate-100">
                {dash.blocks.serviceActivations.recent.map((s) => (
                  <li key={s.id} className="px-4 py-3">
                    <p className="text-sm font-medium text-slate-900">{s.deceasedName}</p>
                    <p className="text-xs text-slate-500">
                      {s.planName} · {s.status}
                      {s.dateOfService
                        ? ` · ${new Date(s.dateOfService).toLocaleDateString('es-VE')}`
                        : ''}
                    </p>
                  </li>
                ))}
              </ul>
            </Card>
          )}

          {showCaskets && dash.blocks.inventoryByModel.length > 0 && (
            <Card className="overflow-hidden border-slate-200/80 p-0 shadow-soft">
              <div className="border-b border-slate-100 bg-slate-50/80 px-4 py-3">
                <h3 className="text-sm font-semibold text-slate-900">Inventario por modelo</h3>
              </div>
              <ul className="divide-y divide-slate-100">
                {dash.blocks.inventoryByModel.map((row) => (
                  <li
                    key={row.model}
                    className="flex items-center justify-between px-4 py-2.5 text-sm"
                  >
                    <span className="flex items-center gap-2 text-slate-800">
                      {row.model}
                      {row.low && (
                        <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-900">
                          Bajo
                        </span>
                      )}
                    </span>
                    <span className="text-slate-600">
                      {row.available}/{row.total} disp.
                    </span>
                  </li>
                ))}
              </ul>
            </Card>
          )}

          {showPlans && dash.blocks.topPlanTemplates.length > 0 && (
            <Card className="overflow-hidden border-slate-200/80 p-0 shadow-soft">
              <div className="border-b border-slate-100 bg-slate-50/80 px-4 py-3">
                <h3 className="text-sm font-semibold text-slate-900">Plantillas más contratadas</h3>
              </div>
              <ul className="divide-y divide-slate-100">
                {dash.blocks.topPlanTemplates.map((t) => (
                  <li
                    key={t.planTemplateId}
                    className="flex items-center justify-between px-4 py-2.5 text-sm"
                  >
                    <span className="truncate text-slate-800">{t.name}</span>
                    <span className="ml-2 shrink-0 rounded-md bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700">
                      {t.activePlansCount}
                    </span>
                  </li>
                ))}
              </ul>
            </Card>
          )}

          {showAudit && dash.blocks.teamActivity.length > 0 && (
            <Card className="overflow-hidden border-slate-200/80 p-0 shadow-soft">
              <div className="border-b border-slate-100 bg-slate-50/80 px-4 py-3">
                <h3 className="text-sm font-semibold text-slate-900">Actividad del equipo</h3>
                <p className="text-xs text-slate-500">Últimos eventos de auditoría</p>
              </div>
              <ul className="max-h-64 overflow-y-auto divide-y divide-slate-100">
                {dash.blocks.teamActivity.map((a) => (
                  <li key={a.id} className="px-4 py-3">
                    <p className="text-xs text-slate-500">
                      {new Date(a.createdAt).toLocaleString('es-VE')} · {a.actor?.name || 'Sistema'}
                    </p>
                    <p className="mt-0.5 text-sm text-slate-800 line-clamp-2">{a.description}</p>
                  </li>
                ))}
              </ul>
              <div className="border-t border-slate-100 p-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full"
                  onClick={() => navigate('/configuration/audit')}
                >
                  Historial completo
                </Button>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
